import React from "react";
import { chartComponentFor } from "./chartRegistry";
import { fmtMoney, fmtPct, fmtSignedMoney, fmtSignedPct } from "../investigations/format";
import FollowupChips from "./FollowupChips";

function ScopePills({ scope }) {
  if (!scope) return null;
  const pills = [scope.period, scope.comparisonPeriod && `vs ${scope.comparisonPeriod}`, scope.currency, scope.dataAsOf && `as of ${scope.dataAsOf}`].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {pills.map((p) => (
        <span key={p} className="text-xs text-text-2 bg-surface-2 px-2 py-0.5 rounded">{p}</span>
      ))}
    </div>
  );
}

function VerificationBadge({ verification }) {
  if (!verification) return null;
  const tone = { verified: "text-success", limited: "text-warning", estimated: "text-text-2", unable: "text-danger" }[verification.status] || "text-text-2";
  return <span className={`text-xs font-medium ${tone}`}>{verification.status}</span>;
}

export default function AnswerCard({ response, onNavigate = () => {}, onFollowup = () => {} }) {
  const { scope, answer, metrics = [], visualizations = [], evidence = [], verification, navigation_actions = [], limitations = [], suggested_followups = [] } = response;
  return (
    <div className="rounded-xl border border-card-bd bg-card p-4 flex flex-col gap-3">
      <div>
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-text">{answer?.headline}</h3>
          <VerificationBadge verification={verification} />
        </div>
        <ScopePills scope={scope} />
        {answer?.summary && <p className="text-sm text-text-2 mt-2">{answer.summary}</p>}
        {answer?.provisional && <p className="text-xs text-text-3 mt-1">Wording auto-generated; figures are exact.</p>}
      </div>

      {metrics.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {metrics.map((m) => {
            const isMoney = m.format === "money";
            const isPercent = m.format === "percent";
            const value = isMoney ? fmtMoney(m.value) : isPercent ? fmtPct(m.value) : m.value;
            const hasDelta = m.delta !== undefined && m.delta !== null;
            const delta = hasDelta ? (isMoney ? fmtSignedMoney(m.delta) : isPercent ? fmtSignedPct(m.delta) : m.delta) : null;
            return (
              <div key={m.label} className="min-w-[120px]">
                <div className="text-xs text-text-3">{m.label}</div>
                <div className="text-lg font-semibold text-text">{value}</div>
                {hasDelta && (
                  <div className={`text-xs ${Number(m.delta) < 0 ? "text-success" : "text-text-2"}`}>{delta}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {visualizations.map((v, i) => {
        const Chart = chartComponentFor(v.type);
        return Chart ? (
          <div key={i}>
            {v.title && <div className="text-xs text-text-3 mb-1">{v.title}</div>}
            <Chart data={v.data} />
          </div>
        ) : null;
      })}

      {evidence.length > 0 && (
        <ul className="text-sm text-text-2">
          {evidence.map((d, i) => (
            <li key={i} className="flex justify-between border-b border-border py-1">
              <span>{d.label}</span>
              <span className={d.deltaCost >= 0 ? "text-text" : "text-success"}>{fmtMoney(d.deltaCost)}</span>
            </li>
          ))}
        </ul>
      )}

      {navigation_actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {navigation_actions.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={() => onNavigate(a)}
              className="text-sm px-3 py-1 rounded-lg border border-border text-text-2 hover:bg-surface-2"
            >
              {a.label}<span aria-hidden="true"> →</span>
            </button>
          ))}
        </div>
      )}

      {limitations.length > 0 && (
        <ul className="text-xs text-text-3 list-disc pl-4">
          {limitations.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}

      <FollowupChips items={suggested_followups} onPick={onFollowup} />
    </div>
  );
}
